import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import type { AxiosError, AxiosRequestConfig } from "axios";
import http from "../../utils/http";

/**
 * RTK Query base query that goes through the app's existing axios instance.
 *
 * WHY NOT `fetchBaseQuery`. `src/utils/http.ts` carries two things this
 * application cannot work without: the access-token refresh on HTTP 498 (a
 * non-standard status this platform uses for an expired token) and the
 * `Accept-Language` header that drives server-side i18n. A base query built on
 * `fetch` would bypass both, and the symptom would be users being logged out
 * mid-session on randomization screens only — the hardest kind of bug to
 * attribute.
 *
 * So RTK Query is adopted for what spec 21.8 asks of it — caching, tag
 * invalidation and generated hooks — while the transport stays the one the
 * rest of the app already uses.
 *
 * @category Randomization
 */
export type AxiosBaseQueryArgs = {
  url: string;
  method?: AxiosRequestConfig["method"];
  data?: unknown;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
};

export type AxiosBaseQueryError = {
  status?: number;
  /** The i18n key the server returned, from spec section 23. */
  message?: string;
  /** Field-level validation errors, for Formik mapping (spec 21.8). */
  data?: unknown;
};

export const axiosBaseQuery =
  (): BaseQueryFn<AxiosBaseQueryArgs, unknown, AxiosBaseQueryError> =>
  async ({ url, method = "GET", data, params, headers }) => {
    try {
      const result = await http({ url, method, data, params, headers });
      // The platform wraps every response as { data, message, statusCode }.
      // Unwrapped here so an endpoint's `transformResponse` is not needed on
      // every single definition.
      return { data: result.data?.data ?? result.data };
    } catch (axiosError) {
      const error = axiosError as AxiosError<{
        message?: string;
        /** Where this platform actually puts a BaseException's `data`. */
        metadata?: unknown;
        data?: unknown;
      }>;
      const body = error.response?.data;
      return {
        error: {
          status: error.response?.status,
          message: body?.message ?? error.message,
          // ERROR PAYLOADS ARRIVE UNDER `metadata`, NOT `data`.
          //
          // `ErrorResponseBuilder.buildAndThrowWithBaseException` builds the
          // body as `{ message, error, metadata }` and puts everything passed
          // to `BaseException.setData()` into `metadata`. Reading `.data` here
          // returned undefined for every refusal this platform raises, so:
          //
          //   - `mapServerErrors` saw no `errors` array and could map nothing
          //     to a Formik field. Eight field-level validation errors
          //     collapsed into one generic "This configuration is not valid"
          //     banner with nothing highlighted - which is what made a
          //     rejected block length impossible to diagnose from the screen.
          //   - `randomizationErrorMessage` had no interpolation parameters,
          //     so any message with a placeholder rendered it literally:
          //     "No randomization slots remain for {{stratum}}".
          //
          // `data` is kept as a fallback: a few endpoints outside this
          // platform's builder do return that shape.
          data: body?.metadata ?? body?.data,
        },
      };
    }
  };
